import { ViewUpdateTpr } from './../../view-update-tpr.class';
import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';

import { ViewUpdateTprApiService } from '../../../../../../modules/api/view-update-tpr-api.service';

import { FormControl } from '@angular/forms';
import { filter, defaultIfEmpty, map, finalize, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { TprecmAuthorizationService } from '../../../authorization/tprecm-authorization.service';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';

enum RiskType {
    GREEN = 'Green',
    RWOP = 'R w/o P',
    RWP = 'R w P'
}

const _greenClass = 'Green';
const _redClass = 'Red';

interface Source {
    designSources?: any[];
    constSources?: any[];
    firstUserSources?: any[];
    userSources?: any[];
    fixtureLocs?: any[];
};

@Component({
    selector: 'tpr-general-info',
    templateUrl: './tpr-general-info.template.html',
    styleUrls: ['../../../tprecm.style.scss', './tpr-general-info.scss']

})
export class TprGeneralInfoComponent {

    leftMenuToggle = true;

    disabled = false;
    _selectedTpr: ViewUpdateTpr;

    get selectedTpr(): ViewUpdateTpr {
        return this._selectedTpr;
    }

    @Input()
    set selectedTpr(value: ViewUpdateTpr) {
        this.disabled = false;
        if (value) {
            this._selectedTpr = value;
        } else {
            this.disabled = true;
            this._selectedTpr = new ViewUpdateTpr();
        }
    }

    @Input()
    set lists(value: Source) {
        this.sources = value || {};
    }

    @Input()
    reSourced = false;

    @Input()
    inActive = false;

    @Output()
    updateEvent: EventEmitter<any> = new EventEmitter<any>();

    RiskType = RiskType;

    sources: Source = {};

    rStudyAdjList: any[] = Array(21).fill(0).map((_, index) => index);

    constructor(
        private readonly viewUpdateTprApiService: ViewUpdateTprApiService,
        private readonly auth: TprecmAuthorizationService
    ) { }

    onDesignSourceSelectionChange(val: MatSelectChange) {
        this.selectedTpr.designSourceId = val.value;
        this.onSpecificFieldChange('designSourceId', this.selectedTpr.designSourceId, true);
    }

    onConstSourceSelectionChange(val: MatSelectChange) {
        this.selectedTpr.constSourceId = val.value;
        this.onGenericFieldChange('constSourceId', this.selectedTpr.constSourceId, false);
    }

    onFirstUserSourceSelectionChange(val: MatSelectChange) {
        this.selectedTpr.firstUserSourceId = val.value;
        this.onGenericFieldChange('firstUserSourceId', this.selectedTpr.firstUserSourceId, true);
    }

    onUserSourceSelectionChange(val: MatSelectChange) {
        this.selectedTpr.userSourceId = val.value;
        this.onSpecificFieldChange('userSourceId', this.selectedTpr.userSourceId, false);
    }

    onFixtureLocSelectionChange(val: MatSelectChange) {
        this.selectedTpr.fixtLocId = val.value;
        this.onGenericFieldChange('fixtLocId', this.selectedTpr.fixtLocId, false);
    }

    onFirstUserReqDateChange() {
        this.onGenericFieldChange('fixtureRequired', this.selectedTpr.fixtureRequired, true);
    }

    onFirstUserReqCheckChange() {
        this.onGenericFieldChange('overrideTm', this.selectedTpr.overrideTm, true);
    }

    onRStudyPassingDateChange() {
        this.onGenericFieldChange('rStudyActualDate', this.selectedTpr.rStudyActualDate, true);
    }

    onRStudyPassingCheckChange() {
        this.onGenericFieldChange('na1', this.selectedTpr.na1, true);
    }

    onRStudyAdjSelectionChange() {
        this.onGenericFieldChange('rStudyWeeksAdj', this.selectedTpr.rStudyWeeksAdj, true);
    }

    onDesignOnlyCheckChange() {
        this.onGenericFieldChange('designOnly', this.selectedTpr.designOnly, true);
    }

    onBuildQuoteCheckChange() {
        this.onGenericFieldChange('quotePrice', this.selectedTpr.quotePrice, false);
    }

    onRationaleCheckChange() {
        this.onGenericFieldChange('quoteTiming', this.selectedTpr.quoteTiming, false);
    }

    onRiskFirstUserCheckChange() {
        this.onGenericFieldChange('r1', this.selectedTpr.r1, true);
    }

    onArrvFirstUserDateChange() {
        this.onGenericFieldChange('arrvTryout', this.selectedTpr.arrvTryout, true);
    }

    onArrvUserSourceDateChange() {
        this.onGenericFieldChange('arrvUserSrc', this.selectedTpr.arrvUserSrc, true);
    }

    onDieTypeChange() {
        this.onGenericFieldChange('progMake', this.selectedTpr.progMake, false);
    }

    fieldAuthorized(field: string) {
        return this.auth.getAuthorizationByField(field);
    }

    updateFieldChange(serviceCall: (params: any) => Observable<any>, fieldName: string, fieldValue: any, calcFields = false) {

        this.disabled = true;
        const params = {
            toolId: this.selectedTpr.toolId,
            tprToolId: this.selectedTpr.tprToolId,
            calcFieldsRequired: calcFields,
            fieldName,
            fieldValue
        };
        serviceCall.bind(this.viewUpdateTprApiService)(params)
            .pipe(
                filter(Boolean),
                defaultIfEmpty({ data: [], totalCount: 0 }),
                map((response: any) => response.data[0]),
                finalize(() => this.disabled = false)
            ).subscribe(
                (updatedTpr: any) => this.updateEvent.emit(updatedTpr),
                (err: Error) => this.updateEvent.emit()
            );
    }

    onGenericFieldChange(fieldName, fieldValue, calcFields) {
        this.updateFieldChange(this.viewUpdateTprApiService.updateGenericTprField, fieldName, fieldValue, calcFields);
    }

    onSpecificFieldChange(fieldName, fieldValue, calcFields = false) {
        this.updateFieldChange(this.viewUpdateTprApiService.updateSpecificTprField, fieldName, fieldValue, calcFields);
    }

    dieTypeEnabled() {
        return ['Single Part', 'White Light Dedi', 'White Light Univ', 'W/L Ded w/EDC',
            'Blue Light Dedi', 'Blue Light Univ', 'W/L Univ w/EDC'].some(typeName => typeName === this.selectedTpr.typeName);
    }

    firstUserDateEnabled() {
        return (this.selectedTpr.arrvTryout || this.selectedTpr.firstUserSource === 'NONE') &&
            (this.selectedTpr.rStudyActualDate || this.selectedTpr.na1);
    }

    getRisk1Calc() {
        return this.selectedTpr.risk1Calc ?
            this.selectedTpr.r1 ? RiskType.RWP : this.selectedTpr.risk1Calc :
            '';
    }
}
